import { get, httpDelete, post, put } from 'api/client';
import { isPostWorkflowApiNew } from 'app/config/postWorkflowApiMode';
import { Post, PublicPostDetail } from 'types/Post';
import { PostStatus } from '@kontentino/kontentino-constants/Posts';
import config from 'config';
import {
  constructFormState,
  PostComposerFormState,
} from 'app/modules/posts/modules/post/components/composer/utils/postComposerFormState';
import {
  PostDetail,
  PostPatch,
  PostPatchStatus,
  PostTranslation,
} from 'types/PostDetail';
import { LinkInfo } from 'types/LinkInfo';
import { jsonToFormData } from 'utils/formData';
import { createSearchFromParams, openBlank, prependHttp } from 'utils/url';
import { ActivityLog } from 'types/ActivityLog';
import { sanitizeNotAllowedMediumKeys } from 'utils/post';
import { PostExportVariant } from 'constants/post';
import { EXTENDED_TIMEOUT } from 'constants/request';
import { getExtendedTimeout } from 'utils/request';
import {
  CreateTranslationJobResponse,
  TranslationJobResponse,
} from 'types/AiHubTranslation';
import DateUtils from 'app/utils/date';
import { CancelToken } from 'axios';
import { User } from 'types/User';

export type AssignTasksParams = {
  posts: number[];
  users: number[];
  message: string;
  isUrgent: boolean;
  canSendEmail: boolean;
};

export type ScheduleParams = {
  ids: number[];
  chosenMethod: 'publish' | 'export' | 'publishNow';
  confirmRepublish?: boolean;
};

export const ASSET_POST_PUBLISHING_NOT_YET_SUPPORTED =
  'asset_post_publishing_not_yet_supported';

export type ScheduleResponse = Record<
  string,
  {
    status: boolean;
    message?: string;
    data?: string | unknown[];
    /** Typed failure reason, e.g. ASSET_POST_PUBLISHING_NOT_YET_SUPPORTED. */
    code?: string;
  }
>;

export type SchedulePublishNowParams = {
  id: number;
  date: string;
  time: string;
  confirmRepublish?: boolean;
};

export type UpdatePostParams = {
  id: number;
  fields: Partial<Post>;
};

export type ToggleAssignedTaskCompletionParams = {
  postId: number;
  userId: number;
  taskId: number;
  completed: boolean;
};

type FetchLinkInfoParams = {
  pageId: number;
  url: string;
  cancelToken?: CancelToken;
};

type CheckLinkDomainVerificationParams = FetchLinkInfoParams;

type ExportContentsParams = {
  pages: number[];
  type: PostExportVariant;
  dateFrom: string;
  dateTo: string;
  selectedPosts: number[];
  statuses: number[];
  types: string[];
  labels: number[];
  users: number[];
  project: number[];
};

export type PostScheduleValidationMessage = {
  message: string;
  code: number;
};

export type PostScheduleValidation = {
  postId: number;
  messages: {
    errors: PostScheduleValidationMessage[];
    warnings: PostScheduleValidationMessage[];
  };
  canExport: boolean;
  canSchedule: boolean;
  canPublishNow: boolean;
  canPublishDarkPost: boolean;
};

export type TrashPostsParams = {
  profileIds?: number[];
  page?: number;
  perPage?: number;
};

export type TrashPost = {
  id: number;
  type: number;
  subtype: number;
  time: string | null;
  date: string | null;
  text: string;
  created_at: string;
  deleted_at: string;
  page: {
    id: number;
    name: string;
    type: number;
    logo: string | null;
  };
  tags: {
    id: number;
    name: string;
    color: string;
  }[];
  preview_image: string | null;
  isGlobal: boolean;
};

export type TrashPostsResponse = {
  data: TrashPost[];
  meta: {
    total: number;
    per_page: number;
    current_page: number;
    last_page: number;
  };
};

const SAVE_POST_CONFIG = {
  baseURL: config.API_SERVICE_ENDPOINT,
  timeout: getExtendedTimeout(10),
};

export type RetryFailedPostOutcome =
  | 'requeued'
  | 'already_published'
  | 'not_retryable';

export interface RetryFailedPostResponse {
  outcome: RetryFailedPostOutcome;
  record_id: string | null;
}

const PostApi = {
  save(form: PostComposerFormState): Promise<PostDetail> {
    const {
      id,
      date,
      project,
      page,
      tags,
      audienceRestrictions,
      translationText,
      distributeToPageIds,
      wasPublished: _wasPublished,
      ...rest
    } = form;

    const requestData = sanitizeNotAllowedMediumKeys({
      ...rest,
      date: date ? DateUtils.toDateString(date) : null,
      pageId: page.id,
      projectId: project?.id,
      tags: tags.map((tag) => tag.id),
      budget: Number.isFinite(Number(rest.budget)) ? Number(rest.budget) : null,
      translation_text: translationText.trim(),
      audience_restrictions: audienceRestrictions,
      distributeToPageIds: distributeToPageIds,
    });

    if (!!id) {
      return put(`posts/${id}`, requestData, SAVE_POST_CONFIG);
    }

    return post('posts', requestData, SAVE_POST_CONFIG);
  },
  async savePartial({
    postId,
    values,
  }: {
    postId: number;
    values: Partial<PostComposerFormState> & {
      shouldUpdateComments?: boolean;
      markAsDone?: boolean;
      source?: string;
      confirmRepublish?: boolean;
    };
  }): Promise<PostDetail> {
    const post = await PostApi.detail(postId);
    const formState = constructFormState({ post });

    return PostApi.save({
      ...formState,
      ...values,
    });
  },
  detail(postId: number): Promise<PostDetail> {
    return get<PostDetail>(`/posts/${postId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  publicPreview(id: string | number, token: string): Promise<PublicPostDetail> {
    return get(`post/${id}`, {
      baseURL: config.PUBLIC_API_SERVICE_ENDPOINT,
      params: { token },
    });
  },
  publicPreviewUrl(ids: number | number[]): Promise<string> {
    return get(
      `posts/previewLink?${(Array.isArray(ids) ? ids : [ids])
        .map((id) => `postIds[]=${id}`)
        .join('&')}`,
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  schedule({
    ids,
    chosenMethod,
    confirmRepublish,
  }: ScheduleParams): Promise<ScheduleResponse> {
    const data = {
      postIds: ids,
      chosenMethod,
      ...(confirmRepublish ? { confirmRepublish: true } : {}),
    };

    return post('/posts/schedule', data, {
      timeout: getExtendedTimeout(5),
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  scheduleDarkPost(id: number): Promise<{ id: string }> {
    const data = new FormData();

    data.append('id', id.toString());

    return post('/posts2/ajaxCreateUnpublishedPagePost', data, {
      timeout: getExtendedTimeout(5),
    });
  },
  createDarkPost(id: number): Promise<{ id: string }> {
    return post(`/posts/${id}/dark-post`, undefined, {
      timeout: getExtendedTimeout(5),
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  publishNow(params: SchedulePublishNowParams): Promise<{ id: string }> {
    const data = new FormData();

    data.append('id', params.id.toString());
    data.append('date', params.date);
    data.append('time', params.time);

    if (params.confirmRepublish) {
      data.append('confirmRepublish', '1');
    }

    return post('/posts2/ajaxPublishPostNow', data, {
      timeout: getExtendedTimeout(5),
    });
  },
  validate(postIds: number[]): Promise<PostScheduleValidation[]> {
    const batchSize = 20;
    const batches: Promise<PostScheduleValidation[]>[] = [];

    for (let i = 0; i < postIds.length; i += batchSize) {
      const batch = postIds.slice(i, i + batchSize);
      batches.push(
        get<ApiResponse<PostScheduleValidation[]>>(
          `/posts/validate?postIds=${batch.join(',')}`,
          {
            baseURL: config.API_SERVICE_ENDPOINT,
            timeout: getExtendedTimeout(2),
          },
        ).then((res) => res.data),
      );
    }

    return Promise.all(batches).then((results) => results.flat());
  },
  toggleRequirement(postId: number, requirementId: number) {
    const data = new FormData();
    data.append('post_id', postId.toString());
    data.append('post_requirement_id', requirementId.toString());

    return post('posts2/ajaxTogglePostRequirementChecked', data);
  },
  toggleAssignedTaskCompletion(p: ToggleAssignedTaskCompletionParams) {
    if (isPostWorkflowApiNew()) {
      return post(
        `posts/${p.postId}/toggle-assigned-task`,
        {
          completed: p.completed,
        },
        { baseURL: config.API_SERVICE_ENDPOINT },
      );
    }

    const data = new FormData();
    data.append('task_id', p.taskId.toString());
    data.append('user_id', p.userId.toString());
    data.append('completed', p.completed ? `1` : `0`);

    return post('/posts/ajaxToggleAssignedTaskComplete', data);
  },
  fetchLinkInfo(params: FetchLinkInfoParams): Promise<LinkInfo> {
    return post(
      '/posts/ajaxFetchLink',
      jsonToFormData({
        ...params,
        url: prependHttp(params.url),
      }),
      {
        timeout: EXTENDED_TIMEOUT,
        cancelToken: params.cancelToken,
      },
    ).then((res: any) => {
      res.url = res.url ?? res['og:url'];
      res.title =
        res.title ?? res['og:title'] ?? res.card_tags?.['twitter:title'];

      res.site_name = res.site_name ?? res['og:site_name'];

      res.description =
        res.description ?? res.card_tags?.['twitter:description'];

      res.media = res.media ?? [];

      return res;
    });
  },
  checkLinkDomainVerification(
    params: CheckLinkDomainVerificationParams,
  ): Promise<{
    ownership_permissions: { can_customize_link_posts: boolean };
    id: string;
  }> {
    return post(
      'pages/ajaxCheckFacebookLinkDomainVerification',
      jsonToFormData({
        ...params,
        url: prependHttp(params.url),
      }),
    );
  },
  getActivityLog(postId: number): Promise<ActivityLog> {
    return get<ActivityLog>(`posts/${postId}/activity-log`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  async exportContents(params: ExportContentsParams) {
    const search = createSearchFromParams({
      type: params.type,
      pages: params.pages.join(),
      date_from: params.dateFrom,
      date_to: params.dateTo,
      project: params.project.length === 0 ? '-1' : params.project.join(),
      posts: params.selectedPosts.join(),
      status: params.statuses.join(),
      types: params.types?.join(),
      labels: params.labels.join(),
      users: params.users.join(),
    });

    openBlank(`/posts2/exportPosts${search}`);
  },
  copyPostToDate(postId: number, date: string): Promise<Post> {
    const params = new FormData();
    params.append('id', postId.toString());
    params.append('date', date);

    return post('/posts/ajaxCopyPostToDate', params);
  },
  clearPostDate(postId: number): Promise<Post> {
    const params = new FormData();
    params.append('id', postId.toString());

    return post('/posts/ajaxClearPostDate', params);
  },
  editPostStatus(params: {
    id: number;
    status: PostStatus;
    markAsDone?: boolean;
  }): Promise<Post> {
    if (isPostWorkflowApiNew()) {
      return PostApi.savePartial({
        postId: params.id,
        values: { status: params.status, markAsDone: params.markAsDone },
      }) as unknown as Promise<Post>;
    }

    const data = new FormData();

    data.append('id', params.id.toString());
    data.append('status', params.status.toString());

    return post('/posts/ajaxUpdatePostStatus', data);
  },
  getStatusOfScheduledPost(
    id: number,
  ): Promise<{ count: number; fb_posts: number; status: boolean }> {
    return get('/posts2/ajaxGetStatusOfScheduledPost', { params: { id } });
  },
  deletePost(id: number, deleteLocalPosts?: boolean) {
    const params = new FormData();
    params.append('postId', id.toString());

    if (deleteLocalPosts) {
      params.append('forceDeleteLocalPosts', '1');
    }

    return httpDelete(`/posts/${id}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      data: params,
    });
  },
  getPostRevisions(postId: number): Promise<
    {
      id: number;
      user: User;
      hasAttachments: boolean;
      createdAt: string;
    }[]
  > {
    return get(`posts/${postId}/revisions`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  getPostRevisionDetail(
    postId: number,
    revisionId: number,
  ): Promise<PostDetail> {
    return get<PostDetail>(`posts/${postId}/revisions/${revisionId}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  async distributeGlobalPostToLocalPages({
    postId,
    pageIds,
    translations,
    planType,
  }: {
    postId: number;
    pageIds: number[];
    translations?: {
      pageId: number;
      sourceLanguage?: string;
      translate: {
        postText?: string;
        firstComment?: string;
        imageText?: string;
      } | null;
    }[];
    planType?: string;
  }): Promise<PostDetail> {
    return post(
      `/posts/${postId}/distribute`,
      {
        distributeToPageIds: pageIds,
        translations: translations?.filter((t) => t.translate),
        ...(planType ? { planType } : {}),
      },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
        timeout: getExtendedTimeout(10),
      },
    );
  },
  async getPatches(postId: number): Promise<PostPatch[]> {
    return get(`/posts/getPatches`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: {
        postId: postId,
      },
    });
  },
  async applyPatches(
    postId: number,
    patches: {
      id: number;
      status: PostPatchStatus;
    }[],
  ): Promise<{
    status: boolean;
    data: boolean;
  }> {
    return post(
      `/posts/${postId}/applyPatches`,
      {
        patches,
      },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async getTranslation(postId: number) {
    return get<{ data: PostTranslation | null }>(
      `/posts/${postId}/translation`,
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async retryTranslation(postId: number) {
    return post<{ message: string; data: { status: string } }>(
      `/posts/${postId}/translation/retry`,
      {},
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async retryFailedImages(postId: number) {
    return post<{ message: string; data: { status: string } }>(
      `/posts/${postId}/translation/retry-failed-images`,
      {},
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async translatePost(
    postId: number,
    params: {
      targetLanguage: string;
      translatePostText: boolean;
      translateFirstComment: boolean;
      translateImageText: boolean;
    },
  ) {
    return post<{ message: string; data: { status: string } }>(
      `/posts/${postId}/translation`,
      {
        targetLanguage: params.targetLanguage,
        translate: {
          postText: params.translatePostText
            ? params.targetLanguage
            : undefined,
          firstComment: params.translateFirstComment
            ? params.targetLanguage
            : undefined,
          imageText: params.translateImageText
            ? params.targetLanguage
            : undefined,
        },
      },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async createTranslationJob(params: {
    localPostId: number;
    globalPostId?: number;
    targetLanguage: string;
    sourceLanguage?: string;
    albumId?: number;
    translateText?: boolean;
    translateFirstComment?: boolean;
    translateImages?: boolean;
    force?: boolean;
    brandProfileId?: number;
    planType?: string;
  }) {
    const translate: Record<string, string> = {};
    if (params.translateText) {
      translate.postText = params.targetLanguage;
    }
    if (params.translateFirstComment) {
      translate.firstComment = params.targetLanguage;
    }
    if (params.translateImages) {
      translate.imageText = params.targetLanguage;
    }

    return post<{ data: CreateTranslationJobResponse }>(
      `/posts/${params.localPostId}/translation`,
      {
        targetLanguage: params.targetLanguage,
        translate,
        ...(params.force ? { force: true } : {}),
        ...(params.brandProfileId
          ? { brandProfileId: params.brandProfileId }
          : {}),
        ...(params.planType ? { planType: params.planType } : {}),
      },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  getTrashPosts(params?: TrashPostsParams): Promise<TrashPostsResponse> {
    const cleanParams = {
      ...params,
      profileIds:
        params?.profileIds && params.profileIds.length > 0
          ? params.profileIds
          : undefined,
    };
    return get('/posts/trash', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: cleanParams,
    });
  },
  getTrashPostsCount(params?: {
    profileIds?: number[];
  }): Promise<{ count: number }> {
    const cleanParams = {
      ...params,
      profileIds:
        params?.profileIds && params.profileIds.length > 0
          ? params.profileIds
          : undefined,
    };
    return get('/posts/trash/count', {
      baseURL: config.API_SERVICE_ENDPOINT,
      params: cleanParams,
    });
  },
  moveToTrash(id: number): Promise<{ success: boolean; message: string }> {
    return httpDelete(`/posts/${id}`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  restoreFromTrash(id: number): Promise<{ success: boolean; message: string }> {
    return post(`/posts/${id}/restore`, null, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  permanentDeleteFromTrash(
    id: number,
    forceDeleteLocalPosts?: boolean,
  ): Promise<{ success: boolean; message: string }> {
    const params = new FormData();

    if (forceDeleteLocalPosts) {
      params.append('forceDeleteLocalPosts', '1');
    }

    return httpDelete(`/posts/${id}/trash`, {
      baseURL: config.API_SERVICE_ENDPOINT,
      data: params,
    });
  },
  moveMultipleToTrash(postIds: number[]): Promise<{
    success: boolean;
    message: string;
    movedCount: number;
    failedCount: number;
  }> {
    return post(
      '/posts/trash/move-multiple',
      { postIds },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  async getTranslationJobByPostId(postId: number) {
    return get<TranslationJobResponse>(`/posts/${postId}/translation`, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  retryFailedPost(postId: number): Promise<RetryFailedPostResponse> {
    return post(`/posts/${postId}/retry`, undefined, {
      baseURL: config.API_SERVICE_ENDPOINT,
    });
  },
  restoreSelectedFromTrash(postIds: number[]): Promise<{
    success: boolean;
    message: string;
    restoredCount: number;
    failedCount: number;
  }> {
    return post(
      '/posts/trash/restore-selected',
      { postIds },
      {
        baseURL: config.API_SERVICE_ENDPOINT,
      },
    );
  },
  permanentDeleteSelectedFromTrash(
    postIds: number[],
    forceDeleteLocalPosts?: boolean,
  ): Promise<{
    success: boolean;
    message: string;
    deletedCount: number;
    failedCount: number;
  }> {
    return httpDelete('/posts/trash/delete-selected', {
      baseURL: config.API_SERVICE_ENDPOINT,
      data: { postIds, forceDeleteLocalPosts },
    });
  },
};

export default PostApi;
